import { faFlagCheckered, faLocationDot } from "@fortawesome/free-solid-svg-icons";
import type { FeatureCollection } from "geojson";
import mapboxgl from "mapbox-gl";
import React, { useEffect, useMemo, useRef, useState } from "react";
import Map, { Layer, Marker, Popup, Source } from "react-map-gl/mapbox";

import type { EstimateResponse, LatLng } from "../../types/journey";
import { MapMarker } from "../MapMarker/MapMarker";
import { useBoundary } from "./helpers/boundary";
import { FALLBACK_VIEW } from "./helpers/camera";
import { useJourneyCamera } from "./helpers/useJourneyCamera";
import { JourneyLegend } from "./Legend";

type Props = {
  origin: LatLng | null;
  destination: LatLng | null;

  originLabel?: string | null;
  destinationLabel?: string | null;

  estimate: EstimateResponse | null;
  onBoundaryReady?: (boundary: FeatureCollection) => void;

  showLegend?: boolean;
  height?: string;

  /** when user is editing inputs, temporarily hide stale visuals */
  isEditingOrigin?: boolean;
  isEditingDestination?: boolean;
};

const TOOLTIP_HIDE_BREAKPOINT_PX = 720;

export const JourneyMap: React.FC<Props> = ({
  origin,
  destination,
  originLabel,
  destinationLabel,
  estimate,
  onBoundaryReady,
  showLegend = false,
  height = "320px",
  isEditingOrigin = false,
  isEditingDestination = false,
}) => {
  const token = (import.meta.env.VITE_MAPBOX_FRONTEND_ACCESS_TOKEN ?? "").trim();

  const mapRef = useRef<any>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const boundary = useBoundary(onBoundaryReady);

  // suppress stale visuals while editing
  const suppressOrigin = Boolean(origin) && isEditingOrigin;
  const suppressDestination = Boolean(destination) && isEditingDestination;
  const suppressRoute = Boolean(estimate?.route) && (isEditingOrigin || isEditingDestination);

  // track actual container width (for adaptive behavior)
  const [containerWidth, setContainerWidth] = useState<number>(0);

  const isMobile = containerWidth > 0 && containerWidth <= TOOLTIP_HIDE_BREAKPOINT_PX;
  const hasOverlayLegend = showLegend && !isMobile;

  useEffect(() => {
    if (!containerRef.current) return;

    const el = containerRef.current;
    const ro = new ResizeObserver(() => setContainerWidth(el.clientWidth));
    ro.observe(el);
    setContainerWidth(el.clientWidth);

    return () => ro.disconnect();
  }, []);

  // IMPORTANT: Mapbox must be resized when the container changes size (e.g. mobile height swap)
  useEffect(() => {
    const map = mapRef.current?.getMap?.();
    if (!map) return;

    // Resize on next frame so the DOM has applied the new height
    const raf = requestAnimationFrame(() => map.resize());
    return () => cancelAnimationFrame(raf);
  }, [height, containerWidth]);

  // Camera: simple + consistent fitBounds logic
  useJourneyCamera({
    mapRef,
    containerWidth,
    hasLegend: hasOverlayLegend,
    boundary,
    origin,
    destination,
    estimate,
    isEditingOrigin,
    isEditingDestination,
    suppressOrigin,
    suppressDestination,
    suppressRoute,
  });

  const routeGeoJson = useMemo<FeatureCollection | null>(() => {
    if (suppressRoute) return null;

    const coords = estimate?.route?.geometry?.coordinates;
    if (!coords?.length) return null;

    return {
      type: "FeatureCollection",
      features: [
        {
          type: "Feature",
          geometry: {
            type: "LineString",
            coordinates: coords.map(c => [c.lng, c.lat]),
          },
          properties: {},
        },
      ],
    };
  }, [estimate, suppressRoute]);

  const previewGeoJson = useMemo<FeatureCollection | null>(() => {
    if (!origin || !destination) return null;
    if (routeGeoJson) return null;
    if (isEditingOrigin || isEditingDestination) return null;

    return {
      type: "FeatureCollection",
      features: [
        {
          type: "Feature",
          geometry: {
            type: "LineString",
            coordinates: [
              [origin.lng, origin.lat],
              [destination.lng, destination.lat],
            ],
          },
          properties: {},
        },
      ],
    };
  }, [origin, destination, routeGeoJson, isEditingOrigin, isEditingDestination]);

  const congestionGeoJson = useMemo<FeatureCollection | null>(() => {
    if (suppressRoute) return null;

    const segs = estimate?.route?.congestedSegments;
    if (!segs?.length) return null;

    return {
      type: "FeatureCollection",
      features: segs.map((s, idx) => ({
        type: "Feature",
        properties: { congestionLevel: s.congestionLevel, idx },
        geometry: {
          type: "LineString",
          coordinates: s.coordinates.map(c => [c.lng, c.lat]),
        },
      })),
    };
  }, [estimate, suppressRoute]);

  if (!token) {
    return <div style={{ padding: 12 }}>Missing Mapbox token</div>;
  }

  const popupsEnabledByViewport = containerWidth > TOOLTIP_HIDE_BREAKPOINT_PX;

  type Tuple2 = [number, number];
  const pt = (x: number, y: number): Tuple2 => [x, y];

  const POPUP_OFFSETS: React.ComponentProps<typeof Popup>["offset"] = {
    top: pt(0, 2),
    bottom: pt(0, -32),
    left: pt(16, -12),
    right: pt(-16, -12),
    center: pt(-10, 25),
    "top-left": [0, 2],
    "top-right": [0, 2],
    "bottom-right": [0, -30],
    "bottom-left": [0, -30],
  };

  return (
    <div
      ref={containerRef}
      style={{
        width: "100%",
        position: "relative",
        marginInline: "auto",
        paddingInline: 6,
      }}
    >
      <div
        style={{
          height,
          width: "100%",
          position: "relative",
          overflow: "hidden",
          paddingInline: 6,
        }}
      >
        <Map
          ref={mapRef}
          mapboxAccessToken={token}
          mapLib={mapboxgl as any}
          style={{
            width: "100%",
            height: "100%",
            pointerEvents: "none",
            borderRadius: 12,
          }}
          initialViewState={{
            latitude: FALLBACK_VIEW.latitude,
            longitude: FALLBACK_VIEW.longitude,
            zoom: FALLBACK_VIEW.zoom,
          }}
          mapStyle="mapbox://styles/mapbox/outdoors-v12"
          dragPan={false}
          scrollZoom={false}
          doubleClickZoom={false}
          touchZoomRotate={false}
          keyboard={false}
        >
          {boundary && (
            <Source id="bcp-boundary" type="geojson" data={boundary}>
              <Layer
                id="bcp-fill"
                type="fill"
                paint={{ "fill-color": "#3b82f6", "fill-opacity": 0.38 }}
              />
              <Layer
                id="bcp-line"
                type="line"
                paint={{ "line-color": "#3b82f6", "line-width": 2 }}
              />
            </Source>
          )}

          {previewGeoJson && (
            <Source id="preview" type="geojson" data={previewGeoJson}>
              <Layer
                id="preview-line"
                type="line"
                paint={{
                  "line-color": "#4A6FA5",
                  "line-width": ["interpolate", ["linear"], ["zoom"], 5, 3, 12, 6],
                  "line-opacity": 0.9,
                  "line-dasharray": [1.5, 1.5],
                }}
                layout={{ "line-join": "round", "line-cap": "round" }}
              />
            </Source>
          )}

          {routeGeoJson && (
            <Source id="route" type="geojson" data={routeGeoJson}>
              <Layer
                id="route-line"
                type="line"
                paint={{
                  "line-color": "#22c55e",
                  "line-width": ["interpolate", ["linear"], ["zoom"], 5, 3, 12, 7],
                  "line-opacity": 0.85,
                }}
                layout={{ "line-join": "round", "line-cap": "round" }}
              />
            </Source>
          )}

          {congestionGeoJson && (
            <Source id="congestion" type="geojson" data={congestionGeoJson}>
              <Layer
                id="congestion-line"
                type="line"
                paint={{
                  "line-color": [
                    "match",
                    ["get", "congestionLevel"],
                    "low",
                    "#22c55e",
                    "moderate",
                    "#eab308",
                    "heavy",
                    "#f97316",
                    "severe",
                    "#ef4444",
                    "#ef4444",
                  ],
                  "line-width": ["interpolate", ["linear"], ["zoom"], 5, 5, 12, 12],
                }}
              />
            </Source>
          )}

          {/* Origin marker + popup */}
          {origin && !suppressOrigin && (
            <>
              <Marker longitude={origin.lng} latitude={origin.lat} anchor="bottom">
                <MapMarker
                  id="origin-marker"
                  icon={faLocationDot}
                  iconColor="#059669"
                  borderColor="#059669"
                />
              </Marker>

              {popupsEnabledByViewport && (
                <Popup
                  longitude={origin.lng}
                  latitude={origin.lat}
                  offset={POPUP_OFFSETS}
                  closeButton={false}
                  closeOnClick={false}
                  focusAfterOpen={false}
                  className="journeyPopup journeyPopup--origin"
                  maxWidth="320px"
                >
                  {originLabel ?? "Origin"}
                </Popup>
              )}
            </>
          )}

          {/* Destination marker + popup */}
          {destination && !suppressDestination && (
            <>
              <Marker longitude={destination.lng} latitude={destination.lat} anchor="bottom">
                <MapMarker
                  id="destination-marker"
                  icon={faFlagCheckered}
                  iconColor="#0f172a"
                  borderColor="#0f172a"
                />
              </Marker>

              {popupsEnabledByViewport && (
                <Popup
                  longitude={destination.lng}
                  latitude={destination.lat}
                  offset={POPUP_OFFSETS}
                  closeButton={false}
                  closeOnClick={false}
                  focusAfterOpen={false}
                  className="journeyPopup journeyPopup--destination"
                  maxWidth="320px"
                >
                  {destinationLabel ?? "Destination"}
                </Popup>
              )}
            </>
          )}
        </Map>

        {/* Desktop/tablet overlay */}
        {showLegend && !isMobile && (
          <div style={{ position: "absolute", right: 12, bottom: 24 }}>
            <JourneyLegend />
          </div>
        )}
      </div>

      {/* Mobile below */}
      {showLegend && isMobile && (
        <div style={{ paddingInline: 6, marginTop: 10 }}>
          <JourneyLegend />
        </div>
      )}
    </div>
  );
};
